import { createAsyncThunk } from "@reduxjs/toolkit";
import { StripeAccount } from "@switcherstudio/switcher-api-client";
import { RootState } from "store/reducers";
import { AppDispatch } from "store/store";
import {
    resetPollingStartedAt,
    setGatedContentStatus,
    setLoadingStripeAccounts,
    setPollingStartedAtToNow,
    setPollingStripeAccounts,
    setStripeAccount,
    setStripeAccountDetails
} from "./slice";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import rollbar from "helpers/rollbar";
import { Client } from "api/client";
import { IUserInfo } from "store/user/types";

export const getGatedContentStatus = (
    account: StripeAccount,
    disabled: boolean = false
): GatedContentStatus => {
    if (disabled) return GatedContentStatus.FEATURE_DISABLED;

    if (!account || !account.DetailsSubmitted)
        return GatedContentStatus.STRIPE_ACCOUNT_NOT_SETUP;

    if (
        !account.ChargesEnabled ||
        !account.PayoutsEnabled ||
        account.AccountRequirements
    )
        return GatedContentStatus.STRIPE_ACCOUNT_UNVERIFIED;

    return GatedContentStatus.READY;
};

/**
 * Fetches the stripe account for the user and sets it in the store.
 * @param userInfo the user info object of the user to fetch the stripe account for
 * @param dispatch
 * @param client
 */
const _doFetchStripeAccount = async (
    userInfo: IUserInfo,
    dispatch: AppDispatch,
    client: Client
) => {
    const { Details, accounts } = await client.stripeConnect_GetStripeAccount(
        userInfo?.User?.Details?.Id,
        true
    );

    if (accounts && accounts?.length !== 0) {
        dispatch(setStripeAccount(accounts[0]));

        dispatch(
            setGatedContentStatus(
                getGatedContentStatus(
                    accounts[0],
                    !userInfo?.Claims?.Details?.FeatureClaims.includes(
                        "gatedcontent"
                    )
                )
            )
        );
    }

    if (Details && Details?.length !== 0) {
        dispatch(setStripeAccountDetails(Details[0]));
    }
};

/**
 * Refetches the stripe account for the user and sets it in the store. Throws an error the account has not be fetched.
 * @param userInfo the user info object of the user to fetch the stripe account for
 * @param dispatch
 * @param client
 */
export const refreshStripeAccount = createAsyncThunk<
    void,
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>(
    "stripeConnectAccount/refreshStripeAccount",
    async (_, { dispatch, getState }) => {
        if (!getState().stripeConnectAccount.account) {
            throw new Error(
                "No stripe account found. Use getStripeAccount first."
            );
        }

        dispatch(setLoadingStripeAccounts(true));

        const client = new Client({ showLoading: false });

        const { userInfo } = getState().user;

        try {
            await _doFetchStripeAccount(userInfo, dispatch, client);
        } catch (error) {
            rollbar.warning("Error getting stripe account", error);
        } finally {
            dispatch(setLoadingStripeAccounts(false));
        }
    }
);

/**
 * Fetches the stripe account for the user and sets it in the store. Throws an error if the account has already been fetched.
 * @param userInfo the user info object of the user to fetch the stripe account for
 * @param dispatch
 * @param client
 */
export const getStripeAccount = createAsyncThunk<
    void,
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>(
    "stripeConnectAccount/getStripeAccount",
    async (_, { dispatch, getState }) => {
        if (getState().stripeConnectAccount.account) return;

        dispatch(setLoadingStripeAccounts(true));

        const client = new Client({ showLoading: false });

        const { userInfo } = getState().user;

        try {
            await _doFetchStripeAccount(userInfo, dispatch, client);
        } catch (error) {
            rollbar.warning("Error getting stripe account", error);
        } finally {
            dispatch(setLoadingStripeAccounts(false));
        }
    }
);

/**
 * Fetches the stripe account for the user and sets it in the store. Throws an error if the account has already been fetched.
 * @param userInfo the user info object of the user to fetch the stripe account for
 * @param dispatch
 * @param client
 */
export const pollStripeAccount = createAsyncThunk<
    void,
    void,
    {
        dispatch: AppDispatch;
        state: RootState;
    }
>(
    "stripeConnectAccount/pollStripeAccount",
    async (_, { dispatch, getState }) => {
        const client = new Client({ showLoading: false });

        const {
            stripeConnectAccount: {
                gatedContentStatus,
                pollingTimeout,
                startedPollingAt
            }
        } = getState();

        const { userInfo } = getState().user;

        if (!startedPollingAt) {
            dispatch(setPollingStartedAtToNow());
        }

        try {
            dispatch(setPollingStripeAccounts(true));

            await _doFetchStripeAccount(userInfo, dispatch, client);

            const hasTimedOut = startedPollingAt
                ? new Date().getTime() >
                  startedPollingAt.getTime() + pollingTimeout
                : false;

            if (
                gatedContentStatus ===
                    GatedContentStatus.STRIPE_ACCOUNT_UNVERIFIED ||
                gatedContentStatus === GatedContentStatus.READY ||
                hasTimedOut
            ) {
                dispatch(setPollingStripeAccounts(false));
                dispatch(resetPollingStartedAt());
            } else {
                // Poll the stripe account every 3.5 seconds
                setTimeout(async () => {
                    dispatch(pollStripeAccount());
                }, 3500);
            }
        } catch (error) {
            rollbar.warning("Error getting stripe account", error);
            dispatch(setPollingStripeAccounts(false));
        }
    }
);
